import { useQuery, useMutation } from '@apollo/client/react'
import {
  GET_ACCOUNT,
  UPDATE_ACCOUNT,
  DELETE_ACCOUNT,
  SET_DEFAULT_EMAIL_PROVIDER,
  STRIPE_CHECKOUT,
  STRIPE_PORTAL,
  STRIPE_SWITCH_PLAN,
  STRIPE_CANCEL,
  UPDATE_ACCOUNT_PLATFORM,
  DISCONNECT_ACCOUNT_PLATFORM,
  TEST_ACCOUNT_PLATFORM,
  SET_NOTIFICATION_RECIPIENTS,
  SEND_TEST_NOTIFICATION,
  REQUEST_WHATSAPP_VERIFICATION,
  REQUEST_TELEGRAM_CONNECTION,
} from '@/lib/operations/account'
import type {
  NotificationRecipientGql,
  NotificationRecipientInput,
  NotificationTestResult,
  TelegramConnectionInit,
  TestPlatformResult,
  UserAccount,
  WhatsappVerificationInit,
} from '@/lib/types'

// ─── Read hooks ───────────────────────────────────────────────────────────────

export function useAccount() {
  const { data, loading, error } = useQuery<{ account: UserAccount }>(GET_ACCOUNT)
  return { account: data?.account, loading, error }
}

// ─── Account mutation hooks ───────────────────────────────────────────────────

export function useUpdateAccount() {
  const [mutate, { loading }] = useMutation<{ updateAccount: UserAccount }>(UPDATE_ACCOUNT, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const updateAccount = (vars: {
    displayName?: string
    photoUrl?: string
    modelProvider?: string
    modelId?: string
    providerApiKey?: string
  }) => mutate({ variables: vars })
  return { updateAccount, loading }
}

export function useDeleteAccount() {
  const [mutate, { loading }] = useMutation<{ deleteAccount: boolean }>(DELETE_ACCOUNT)
  const deleteAccount = () => mutate()
  return { deleteAccount, loading }
}

export function useSetDefaultEmailProvider() {
  const [mutate, { loading }] = useMutation<{ setDefaultEmailProvider: boolean }, { provider: string }>(
    SET_DEFAULT_EMAIL_PROVIDER,
    { refetchQueries: [{ query: GET_ACCOUNT }] },
  )
  const setDefaultEmailProvider = (provider: string) => mutate({ variables: { provider } })
  return { setDefaultEmailProvider, loading }
}

// ─── Stripe hooks ─────────────────────────────────────────────────────────────

export function useStripeCheckout() {
  const [mutate, { loading }] = useMutation<{ stripeCheckout: { url: string } }>(STRIPE_CHECKOUT)
  const checkout = (planType: string, successUrl: string, cancelUrl: string) =>
    mutate({ variables: { planType, successUrl, cancelUrl } })
  return { checkout, loading }
}

export function useStripePortal() {
  const [mutate, { loading }] = useMutation<{ stripePortal: { url: string } }>(STRIPE_PORTAL)
  const openPortal = (returnUrl: string) => mutate({ variables: { returnUrl } })
  return { openPortal, loading }
}

export function useStripeSwitchPlan() {
  const [mutate, { loading }] = useMutation<{ stripeSwitchPlan: { uid: string; planType: string } }>(STRIPE_SWITCH_PLAN, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const switchPlan = (newPlanType: string) => mutate({ variables: { newPlanType } })
  return { switchPlan, loading }
}

export function useStripeCancelPlan() {
  const [mutate, { loading }] = useMutation<{ stripeCancelSubscription: { uid: string; planType: string } }>(STRIPE_CANCEL, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const cancelPlan = () => mutate()
  return { cancelPlan, loading }
}

// ─── Account platform hooks ───────────────────────────────────────────────────

export function useUpdateAccountPlatform() {
  const [mutate, { loading }] = useMutation<{ updateAccountPlatform: { platform: string; connected: boolean; connectedAt: string } }>(UPDATE_ACCOUNT_PLATFORM, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const updatePlatform = (platform: string, credentials: Record<string, unknown>) =>
    mutate({ variables: { platform, credentials } })
  return { updatePlatform, loading }
}

export function useDisconnectAccountPlatform() {
  const [mutate, { loading }] = useMutation<{ disconnectAccountPlatform: boolean }>(DISCONNECT_ACCOUNT_PLATFORM, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const disconnectPlatform = (platform: string) => mutate({ variables: { platform } })
  return { disconnectPlatform, loading }
}

export function useTestAccountPlatform() {
  const [mutate, { loading, data }] = useMutation<{ testAccountPlatform: TestPlatformResult }>(TEST_ACCOUNT_PLATFORM)
  const testPlatform = (platform: string) => mutate({ variables: { platform } })
  return { testPlatform, loading, result: data?.testAccountPlatform ?? null }
}

// ─── Notification recipient hooks ─────────────────────────────────────────────

export function useSetNotificationRecipients() {
  const [mutate, { loading }] = useMutation<
    { setNotificationRecipients: NotificationRecipientGql[] },
    { recipients: NotificationRecipientInput[] }
  >(SET_NOTIFICATION_RECIPIENTS, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const setRecipients = (recipients: NotificationRecipientInput[]) =>
    mutate({ variables: { recipients } })
  return { setRecipients, loading }
}

export function useSendTestNotification() {
  const [mutate, { loading }] = useMutation<
    { sendTestNotification: NotificationTestResult },
    { recipientId: string }
  >(SEND_TEST_NOTIFICATION)
  const sendTest = (recipientId: string) => mutate({ variables: { recipientId } })
  return { sendTest, loading }
}

export function useRequestWhatsappVerification() {
  const [mutate, { loading }] = useMutation<
    { requestWhatsappVerification: WhatsappVerificationInit },
    { recipientId: string }
  >(REQUEST_WHATSAPP_VERIFICATION)
  const request = (recipientId: string) => mutate({ variables: { recipientId } })
  return { request, loading }
}

export function useRequestTelegramConnection() {
  const [mutate, { loading }] = useMutation<
    { requestTelegramConnection: TelegramConnectionInit },
    { label?: string }
  >(REQUEST_TELEGRAM_CONNECTION, {
    refetchQueries: [{ query: GET_ACCOUNT }],
  })
  const request = (label?: string) => mutate({ variables: { label } })
  return { request, loading }
}
